// Source: CMS Medicare Claims Processing Manual, Chapter 10 (Rev. 13089, 02-21-25)
// https://www.cms.gov/regulations-and-guidance/guidance/manuals/downloads/clm104c10.pdf
//
// Codes relevant to HHA billing are documented in §40.2 (HH PPS Claims):
//   - 47: Transfer from another HHA (also the only condition code listed for the NOA in §40.1)
//   - 54: No skilled HH visits in billing period
//   - DR: Disaster-related waiver of OASIS submission
//   - 20: Demand bill (§50)
//   - 21: No-payment/denial notice bill (§60)
//   - D0–E0: Claim change reason codes (adjustments/cancellations)
//
// The homeHealth flag marks codes called out in §40.2 as applicable to HHA claims.

export interface ConditionCode {
  code: string;
  description: string;
  /** Used on HHA claims (TOB 32X) per CGS Medicare HH Billing Codes guidance */
  homeHealth: boolean;
}

export const CONDITION_CODES: ConditionCode[] = [
  { code: '01', description: 'Military Service Related', homeHealth: false },
  {
    code: '02',
    description: 'Condition Is Employment Related',
    homeHealth: false,
  },
  {
    code: '03',
    description: 'Patient Covered by Insurance Not Reflected Here',
    homeHealth: false,
  },
  { code: '04', description: 'Information Only Bill', homeHealth: false },
  { code: '05', description: 'Lien Has Been Filed', homeHealth: false },
  {
    code: '06',
    description:
      'ESRD Patient — First 30 Months of Entitlement Covered by EGHP',
    homeHealth: false,
  },
  {
    code: '07',
    description: 'Treatment of Non-Terminal Condition for Hospice Patient',
    homeHealth: true,
  },
  {
    code: '08',
    description:
      'Beneficiary Would Not Provide Information Concerning Other Insurance Coverage',
    homeHealth: false,
  },
  {
    code: '09',
    description: 'Neither Patient nor Spouse is Employed',
    homeHealth: false,
  },
  {
    code: '10',
    description: 'Patient and/or Spouse is Employed but No EGHP Exists',
    homeHealth: false,
  },
  {
    code: '11',
    description: 'Disabled Beneficiary but No LGHP',
    homeHealth: false,
  },
  { code: '17', description: 'Patient is Homeless', homeHealth: false },
  { code: '18', description: 'Maiden Name Retained', homeHealth: false },
  {
    code: '19',
    description: "Child Retains Mother's Maiden Name",
    homeHealth: false,
  },
  {
    code: '20',
    description: 'Beneficiary Requested Billing (Demand Bill)',
    homeHealth: true,
  },
  {
    code: '21',
    description: 'Billing for Denial Notice (No-Pay Bill)',
    homeHealth: true,
  },
  {
    code: '24',
    description: 'Home IV Patient Receiving HHA Services',
    homeHealth: true,
  },
  {
    code: '25',
    description: 'Patient is Non-U.S. Resident',
    homeHealth: false,
  },
  {
    code: '26',
    description:
      'VA Eligible Patient Chooses to Receive Services in a Medicare Certified Facility',
    homeHealth: false,
  },
  {
    code: '27',
    description:
      'Patient Referred to a Sole Community Hospital for a Diagnostic Laboratory Test',
    homeHealth: false,
  },
  {
    code: '28',
    description: "Patient and/or Spouse's EGHP is Secondary to Medicare",
    homeHealth: false,
  },
  {
    code: '29',
    description: 'Disabled Beneficiary Under Age 65 with LGHP',
    homeHealth: false,
  },
  { code: '30', description: 'Qualifying Clinical Trials', homeHealth: false },
  {
    code: '31',
    description: 'Patient is Student (Full Time - Day)',
    homeHealth: false,
  },
  {
    code: '32',
    description: 'Patient is Student (Cooperative/Work Study Program)',
    homeHealth: false,
  },
  {
    code: '33',
    description: 'Patient is Student (Full Time - Night)',
    homeHealth: false,
  },
  {
    code: '34',
    description: 'Patient is Student (Part Time)',
    homeHealth: false,
  },
  {
    code: '36',
    description: 'General Care Patient in a Special Unit',
    homeHealth: false,
  },
  {
    code: '37',
    description: "Ward Accommodation at Patient's Request",
    homeHealth: false,
  },
  {
    code: '38',
    description: 'Semi-Private Room Not Available',
    homeHealth: false,
  },
  {
    code: '39',
    description: 'Private Room Medically Necessary',
    homeHealth: false,
  },
  { code: '40', description: 'Same Day Transfer', homeHealth: false },
  { code: '41', description: 'Partial Hospitalization', homeHealth: false },
  {
    code: '42',
    description: 'Continuing Care Not Related to Inpatient Admission',
    homeHealth: false,
  },
  {
    code: '43',
    description:
      'Continuing Care Not Provided Within Prescribed Post-Discharge Window',
    homeHealth: false,
  },
  {
    code: '44',
    description: 'Inpatient Admission Changed to Outpatient',
    homeHealth: false,
  },
  {
    code: '46',
    description: 'Non-Availability Statement on File',
    homeHealth: false,
  },
  {
    code: '47',
    description: 'Transfer from Another Home Health Agency',
    homeHealth: true,
  },
  {
    code: '48',
    description:
      'Psychiatric Residential Treatment Centers for Children and Adolescents',
    homeHealth: false,
  },
  {
    code: '49',
    description: 'Product Replacement Due to Malfunction',
    homeHealth: false,
  },
  {
    code: '50',
    description: 'Product Replacement for Manufacturer Recall',
    homeHealth: false,
  },
  {
    code: '54',
    description: 'No Skilled HH Visits in Billing Period',
    homeHealth: true,
  },
  {
    code: '55',
    description: 'Skilled Nursing Facility (SNF) Bed Not Available',
    homeHealth: false,
  },
  {
    code: '56',
    description: 'Delayed SNF Admission Due to Medical Appropriateness',
    homeHealth: false,
  },
  {
    code: '57',
    description: 'SNF Readmission Within 30 Days',
    homeHealth: false,
  },
  {
    code: '58',
    description: 'Terminated Medicare Advantage Plan Enrollee',
    homeHealth: false,
  },
  { code: '60', description: 'Day Outlier', homeHealth: false },
  { code: '61', description: 'Cost Outlier', homeHealth: false },
  {
    code: '66',
    description: 'Provider Does Not Wish Cost Outlier Payment',
    homeHealth: false,
  },
  {
    code: '67',
    description: 'Beneficiary Elects Not to Use Life-time Reserve (LTR) Days',
    homeHealth: false,
  },
  {
    code: '68',
    description: 'Beneficiary Elects to Use Life-time Reserve (LTR) Days',
    homeHealth: false,
  },
  { code: '69', description: 'IME/GME Costs Only', homeHealth: false },
  {
    code: '70',
    description: 'Self-Administered Epoetin (EPO) / Darbepoetin Alpha',
    homeHealth: false,
  },
  { code: '71', description: 'Full Care in Dialysis Unit', homeHealth: false },
  { code: '72', description: 'Self-Care in Dialysis Unit', homeHealth: false },
  { code: '73', description: 'Self-Care Dialysis Training', homeHealth: false },
  { code: '74', description: 'Home Dialysis', homeHealth: false },
  {
    code: '75',
    description: 'Home Dialysis — Purchased Equipment',
    homeHealth: false,
  },
  {
    code: '76',
    description: 'Back-up In-Facility Dialysis',
    homeHealth: false,
  },
  {
    code: '77',
    description: 'Provider Accepts Primary Payer as Payment in Full',
    homeHealth: false,
  },
  {
    code: '79',
    description: 'CORF Services Provided Off-site',
    homeHealth: false,
  },
  {
    code: '80',
    description: 'Home Dialysis Patient in Nursing Facility',
    homeHealth: false,
  },
  {
    code: 'A0',
    description: 'TRICARE External Partnership Program',
    homeHealth: false,
  },
  { code: 'A1', description: 'EPSDT/CHAP', homeHealth: false },
  {
    code: 'A2',
    description: "Physically Handicapped Children's Program",
    homeHealth: false,
  },
  { code: 'A3', description: 'Special Federal Funding', homeHealth: false },
  { code: 'A4', description: 'Family Planning', homeHealth: false },
  { code: 'A5', description: 'Disability', homeHealth: false },
  {
    code: 'A6',
    description: 'PPV/Medicare Pneumococcal Pneumonia/Influenza 100% Payment',
    homeHealth: true,
  },
  { code: 'A9', description: 'Second Opinion Surgery', homeHealth: false },
  {
    code: 'AA',
    description: 'Abortion Performed Due to Rape',
    homeHealth: false,
  },
  {
    code: 'AB',
    description: 'Abortion Performed Due to Incest',
    homeHealth: false,
  },
  {
    code: 'AC',
    description:
      'Abortion Performed Due to Serious Fetal Genetic Defect, Deformity, or Abnormality',
    homeHealth: false,
  },
  {
    code: 'AD',
    description:
      'Abortion Performed Due to Life Endangering Physical Condition Caused by Pregnancy',
    homeHealth: false,
  },
  {
    code: 'AE',
    description:
      'Abortion Performed Due to Physical Health of Mother (Non-Life-Threatening)',
    homeHealth: false,
  },
  {
    code: 'AF',
    description:
      'Abortion Performed Due to Emotional/Psychological Health of Mother',
    homeHealth: false,
  },
  {
    code: 'AG',
    description: 'Abortion Performed Due to Social or Economic Reasons',
    homeHealth: false,
  },
  { code: 'AH', description: 'Elective Abortion', homeHealth: false },
  { code: 'AI', description: 'Sterilization', homeHealth: false },
  {
    code: 'AJ',
    description: 'Payer Responsible for Co-Payment',
    homeHealth: false,
  },
  { code: 'AK', description: 'Air Ambulance Required', homeHealth: false },
  {
    code: 'AL',
    description: 'Specialized Treatment/Bed Unavailable',
    homeHealth: false,
  },
  { code: 'DR', description: 'Disaster Related', homeHealth: true },
  {
    code: 'M1',
    description:
      'Roster Billed Influenza Virus Vaccine or Pneumococcal Pneumonia Vaccine',
    homeHealth: true,
  },
];
